import { PotIllustration } from "@/app/components/PotIllustration";
import { CareTagIllustration } from "@/app/components/AddActionIllustrations";
import { LeafIcon, RainIcon, WateringCanIcon } from "@/app/components/system/Icons";
import { WeatherGlyph } from "@/app/components/weather/WeatherGlyph";

/** Static mock pots for the decorative garden preview. */
const MOCK_POTS = [
  {
    shape: "terracotta",
    label: "Kitchen window",
    subtitle: "Large · 3 plants",
    plants: [
      { id: "p1", color: "oklch(0.55 0.09 145)" },
      { id: "p2", color: "oklch(0.5 0.12 130)" },
      { id: "p3", color: "oklch(0.58 0.08 155)" },
    ],
  },
  {
    shape: "basket",
    label: "Balcony herbs",
    subtitle: "Medium · 2 plants",
    plants: [
      { id: "p4", color: "oklch(0.52 0.1 140)" },
      { id: "p5", color: "oklch(0.48 0.11 125)" },
    ],
  },
] as const;

/** Feature callouts shown below the garden mockup. */
const FEATURES = [
  {
    title: "Weather-aware watering",
    body: "Reminders adapt to local rain, heat, and frost",
    icon: <RainIcon size={18} stroke={2} />,
    isIllustration: false,
  },
  {
    title: "Scan & identify",
    body: "Snap a care tag or photo",
    icon: <CareTagIllustration />,
    isIllustration: true,
  },
  {
    title: "Species guidance",
    body: "Fertilizer, pruning, and care notes per plant",
    icon: <LeafIcon size={18} stroke={2} />,
    isIllustration: false,
  },
] as const;

/**
 * Decorative right panel for auth screens — static product showcase with
 * garden mockup, floating badges, and feature callouts. No API dependencies.
 */
export function AuthShowcase() {
  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <aside className="auth-panel-showcase" aria-hidden="true">
      <div className="auth-showcase-inner">
        <header className="auth-showcase-hero">
          <h2 className="auth-showcase-hero-title serif-it">
            Never miss a watering again
          </h2>
          <p className="auth-showcase-hero-desc">
            Plantita tracks every pot, adapts reminders to your local weather,
            and identifies plants from a single photo.
          </p>
        </header>

        <div className="auth-showcase-stage">
          <div className="auth-showcase-sky" />

          <div className="auth-showcase-mock">
            <header className="auth-showcase-mock-head">
              <p className="auth-showcase-kicker">{dateLabel}</p>
              <h2 className="auth-showcase-title">My garden</h2>
            </header>

            <div className="auth-showcase-mock-weather">
              <span className="auth-showcase-mock-weather-icon">
                <WeatherGlyph condition="sun" size={20} />
              </span>
              <div>
                <p className="auth-showcase-mock-weather-stat">72°F · Sunny</p>
                <p className="auth-showcase-mock-weather-hint">
                  Rain tomorrow — adjust schedule
                </p>
              </div>
            </div>

            <div className="auth-showcase-pots">
              {MOCK_POTS.map((pot) => (
                <div key={pot.label} className="auth-showcase-pot">
                  <div className="auth-showcase-pot-art">
                    <PotIllustration shape={pot.shape} plants={[...pot.plants]} size="small" />
                  </div>
                  <div className="auth-showcase-pot-meta">
                    <p className="auth-showcase-pot-label">{pot.label}</p>
                    <p className="auth-showcase-pot-sub">{pot.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="auth-showcase-float auth-showcase-float-water">
            <span className="auth-showcase-float-icon">
              <WateringCanIcon size={16} stroke={2} />
            </span>
            <div>
              <p className="auth-showcase-float-stat">2 pots</p>
              <p className="auth-showcase-float-kicker">Water today</p>
              <p className="auth-showcase-float-hint">Before noon</p>
            </div>
          </div>
        </div>

        <ul className="auth-showcase-cards">
          {FEATURES.map((f) => (
            <li key={f.title} className="auth-showcase-card">
              <span className={`auth-showcase-card-icon ${f.isIllustration ? "auth-showcase-card-icon-ill" : ""}`}>
                {f.icon}
              </span>
              <div>
                <p className="auth-showcase-card-title">{f.title}</p>
                <p className="auth-showcase-card-body">{f.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
